import { useRef } from 'react'

import { useMercureSse } from './useMercureSse'

/**
 * Shape of a notification as received from Mercure (event.data JSON).
 * Translation keys are built from type: `${type}.title` and `${type}.message`
 */
export interface NotificationPayload {
  id: string
  type: string
  context?: {
    materialId?: string
    bookingId?: string
    authorId?: string
    circleId?: string
    requestId?: string
    networkId?: string
  }
  /** Audience scope (as whom the user received it): drives the context switch on click. */
  scopeType?: string | null
  scopeIri?: string | null
  scopeName?: string | null
  createdAt: string
  updatedAt: string
  readAt: string | null
  recipient?: { id: string } | null
  readByUsers?: { id: string }[]
  /** Parameters for translation interpolation */
  params?: Record<string, unknown> | null
  /** Legacy: raw title when type is not provided. */
  title?: string
  /** Legacy: raw message when type is not provided. */
  message?: string
}

export interface UseMercureNotificationsOptions {
  /** Topic IRIs from the /mercure-subscribe-token API; must match what the server publishes to. */
  topics: string[]
  mercureToken: string | null
  onNotification: (n: NotificationPayload) => void
  enabled?: boolean
}

/**
 * Subscribes to Mercure using the topic IRIs returned by the API.
 * Uses fetch() + ReadableStream so we can send Authorization header (many hubs
 * reject JWT in query and only accept the header; EventSource cannot set headers).
 * Parses SSE, calls onNotification, reconnects with backoff on error/end.
 */
export function useMercureNotifications({
  topics,
  mercureToken,
  onNotification,
  enabled = true,
}: UseMercureNotificationsOptions): void {
  const onNotificationRef = useRef(onNotification)
  onNotificationRef.current = onNotification

  useMercureSse({
    topics,
    mercureToken,
    enabled,
    onData: (data) => {
      try {
        const notification = JSON.parse(data) as NotificationPayload
        onNotificationRef.current(notification)
      } catch {
        // ignore invalid JSON
      }
    },
  })
}
